import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { apiBaseUrl, prepareAuthHeaders } from "./apiBase";

const unwrap = (res) => {
  if (!res) return null;
  if (typeof res === "object" && "success" in res) {
    return res.success ? res.data : null;
  }
  return res.data ?? res;
};

const unwrapList = (res) => {
  const data = unwrap(res);
  if (!data) return { collections: [], total: 0, page: 1, pageSize: 20, totalPages: 1 };
  return data;
};

export const collectionsApi = createApi({
  reducerPath: "collectionsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl,
    prepareHeaders: (headers) =>
      prepareAuthHeaders(headers, { contentTypeJson: true }),
  }),
  tagTypes: ["Collection", "CollectionList"],
  keepUnusedDataFor: 60,
  refetchOnMountOrArgChange: true,

  endpoints: (builder) => ({
    fetchCollections: builder.query({
      query: (params = {}) => ({
        url: "collections",
        params,
      }),
      transformResponse: unwrapList,
      providesTags: (result) =>
        result?.collections?.length
          ? [
              ...result.collections.map((collection) => ({
                type: "Collection",
                id: collection.id,
              })),
              { type: "CollectionList", id: "LIST" },
            ]
          : [{ type: "CollectionList", id: "LIST" }],
    }),

    fetchCollectionById: builder.query({
      query: (id) => `collections/${id}`,
      transformResponse: unwrap,
      providesTags: (_result, _err, id) => [{ type: "Collection", id }],
    }),

    createCollection: builder.mutation({
      query: (body) => ({ url: "collections", method: "POST", body }),
      invalidatesTags: [{ type: "CollectionList", id: "LIST" }],
    }),

    updateCollection: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `collections/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _err, arg) => [
        { type: "Collection", id: arg?.id },
        { type: "CollectionList", id: "LIST" },
      ],
    }),

    setCollectionStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `collections/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_result, _err, arg) => [
        { type: "Collection", id: arg?.id },
        { type: "CollectionList", id: "LIST" },
      ],
    }),

    addCollectionProducts: builder.mutation({
      query: ({ id, products }) => ({
        url: `collections/${id}/products`,
        method: "POST",
        body: { products },
      }),
      invalidatesTags: (_result, _err, arg) => [
        { type: "Collection", id: arg?.id },
        { type: "CollectionList", id: "LIST" },
      ],
    }),

    removeCollectionProducts: builder.mutation({
      query: ({ id, catalogProductIds }) => ({
        url: `collections/${id}/products`,
        method: "DELETE",
        body: { catalogProductIds },
      }),
      invalidatesTags: (_result, _err, arg) => [
        { type: "Collection", id: arg?.id },
        { type: "CollectionList", id: "LIST" },
      ],
    }),

    reorderCollectionProducts: builder.mutation({
      query: ({ id, positions }) => ({
        url: `collections/${id}/products/positions`,
        method: "PATCH",
        body: { positions },
      }),
      invalidatesTags: (_result, _err, arg) => [
        { type: "Collection", id: arg?.id },
      ],
    }),
  }),
});

export const {
  useFetchCollectionsQuery,
  useFetchCollectionByIdQuery,
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useSetCollectionStatusMutation,
  useAddCollectionProductsMutation,
  useRemoveCollectionProductsMutation,
  useReorderCollectionProductsMutation,
} = collectionsApi;
